import { NOTHING_ON_FILE } from 'components/Appointment/ConfirmAppointmentModal/helper';
import { MEDICAL_GROUPS } from 'components/Profile/MedicalHistory/helper';
import { SUMMARY_BUILDERS, checkedNames } from 'components/Profile/View/helper';
import { hideAllergies } from 'components/Profile/helper';
import { getInsurance, getStepById } from 'pages/Profile/helper';
import { getPractice } from 'library/auth';
import { capitalize } from 'lodash';
import { formatDate, formatPhone } from 'utils/helpers';

const GREETINGS = { 12: 'Good morning', 17: 'Good afternoon', 24: 'Good evening' };

export const getGreeting = (hour = new Date().getHours()) =>
  GREETINGS[Object.keys(GREETINGS).find((end) => hour < Number(end))];

export const getProfileSections = (profile) => ({
  personal: profile?.attributes?.personal_information?.data?.attributes,
  insurance: getInsurance(profile?.attributes),
  medical: profile?.attributes?.medical_history?.data?.attributes,
  practice: profile?.attributes?.practice?.data?.attributes,
});

export const PENDING_MESSAGES = {
  personal: 'Add your contact details so we can reach you',
  insurance: 'Add your insurance so we can verify your coverage',
  get medical() { return `Tell us about any ${hideAllergies() ? '' : 'allergies, '}conditions or medications`; },
};

const join = (parts, separator = ', ') => parts.filter(Boolean).join(separator);

const emptyMessage = (stepId, attributes, rows, completed) =>
  (attributes && SUMMARY_BUILDERS[stepId](attributes)?.empty?.title) ||
  (rows.length ? null : completed ? NOTHING_ON_FILE : PENDING_MESSAGES[stepId]);

const personalRows = (personal) => {
  const { first_name, middle_name, last_name, gender, birth_date, mobile_phone, address_line1, address_line2, city, state, zip_code, emergency_contacts } = personal || {};
  const contact = emergency_contacts?.data?.[0]?.attributes;
  const hideMiddleName = getPractice()?.data_provider === 'velox';

  return [
    { label: 'Name', value: join([first_name, hideMiddleName ? '' : middle_name, last_name], ' '), half: true },
    { label: 'Gender', value: capitalize(gender), half: true },
    { label: 'Phone', value: mobile_phone ? formatPhone(mobile_phone) : '', half: true },
    { label: 'Date of birth', value: birth_date ? formatDate(birth_date, 'medium') : '', half: true },
    { label: 'Emergency contact', value: contact && join([join([contact.first_name, contact.last_name], ' '), contact.relationship], ' · ') },
    { label: 'Address', value: join([address_line1, address_line2, join([join([city, state]), zip_code], ' ')]) },
  ];
};

const medicalRows = (medical) => [
  ...MEDICAL_GROUPS.map(({ name, title }) => ({
    label: title,
    value: checkedNames(medical?.[name]),
    warn: name === 'allergies',
  })),
  { label: 'Changes in last 6 months', value: medical?.medical_notes },
];

export const getProfileCards = ({ personal, medical }, completion = {}) =>
  [
    { id: 'personal', title: 'Information', attributes: personal, rows: personalRows(personal) },
    { id: 'medical', title: 'Medical History', attributes: medical, rows: medicalRows(medical) },
  ].map(({ attributes, ...card }) => {
    const { icon, iconBg, iconColor } = getStepById(card.id);
    const rows = card.rows.filter(({ value }) => value?.length);
    const completed = Boolean(completion[card.id]);

    return { ...card, rows, completed, empty: emptyMessage(card.id, attributes, rows, completed), icon, iconBg, iconColor };
  });

export const CARD = 'bg-white border border-gray-200 rounded-2xl';
export const CARD_HEADER = 'flex items-center gap-2.5 border-b border-gray-100 bg-secondary-50/70 px-4 py-3 sm:px-5';
export const CARD_TITLE = 'text-[15px] font-bold text-secondary-900';
export const CARD_EMPTY = 'px-4 py-5 text-center text-[13px] text-secondary-400 sm:px-5';
export const PILL = 'inline-flex max-w-full items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-semibold';
export const PILL_COLORS = {
  warn: 'border-red-100 bg-red-50 text-red-600',
  default: 'border-gray-200 bg-secondary-50 text-secondary-700',
};
